import { useState, useEffect, useMemo } from 'react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import ExportButtons from '../components/ExportButtons'
import DateRangePreset from '../components/DateRangePreset'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { ArrowUp, ArrowDown, ChevronsUpDown, Table2, LineChart as LineChartIcon } from 'lucide-react'
import BrandPeriodChart from '../components/BrandPeriodChart'
import { samePeriodLastYear } from '../utils/comparisonPeriod'
import { ColumnDefinition } from '../utils/exportToCsv'
// import PeriodInfoBanner from '../components/PeriodInfoBanner'
import QueryExplanationBox, { buildExplanationText } from '../components/QueryExplanationBox'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../i18n/apiError'


// Helper function to safely parse numeric values
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

interface BrandComparison {
  rank: number
  brand: string
  current_sales: number
  current_market_share: number
  previous_sales: number
  previous_market_share: number
  change_percentage: number | null
}

export default function BrandYoYComparison() {
  const { t } = useTranslation('analytics')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCurrentPreset, setSelectedCurrentPreset] = useState('')
  const [selectedPrevPreset, setSelectedPrevPreset] = useState('')
  const [brands, setBrands] = useState<BrandComparison[]>([])
  const [activeTab, setActiveTab] = useState<'tabell' | 'graf'>('tabell')
  
  // Sort state
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  
  // Vehicle filter
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const maxDataDate = useMaxDataDate()

  // Effective max date: use the actual max SVV registration date
  const effectiveMaxDateStr = maxDataDate

  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('brand-yoy-comparison:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  // Get today's date and dates from last year
  const today = new Date()
  const lastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
  
  const formatDate = (date: Date) => {
    try {
      return date.toISOString().split('T')[0]
    } catch (err) {
      console.error('Date formatting error:', err)
      return '2025-01-01'
    }
  }
  
  // Compare current year vs last year
  const [currentEndDate, setCurrentEndDate] = useState<string>(() => effectiveMaxDateStr ?? formatDate(today))
  const [currentStartDate, setCurrentStartDate] = useState<string>(`${today.getFullYear()}-01-01`)
  const [prevEndDate, setPrevEndDate] = useState<string>(() => {
    if (effectiveMaxDateStr) {
      const maxDate = new Date(effectiveMaxDateStr + 'T12:00:00')
      const prevYear = new Date(maxDate)
      prevYear.setFullYear(prevYear.getFullYear() - 1)
      return prevYear.toISOString().split('T')[0]
    }
    return formatDate(lastYear)
  })
  const [prevStartDate, setPrevStartDate] = useState<string>(`${lastYear.getFullYear()}-01-01`)

  // Queried-state tracking — only update when fetchData actually runs
  const [queriedCurrentStart, setQueriedCurrentStart] = useState(currentStartDate)
  const [queriedCurrentEnd, setQueriedCurrentEnd] = useState(currentEndDate)
  const [queriedPrevStart, setQueriedPrevStart] = useState(prevStartDate)
  const [queriedPrevEnd, setQueriedPrevEnd] = useState(prevEndDate)
  const [queriedVehicleFilter, setQueriedVehicleFilter] = useState(vehicleFilter)
  const [queriedPostnummer, setQueriedPostnummer] = useState(minePostnummer)

  // Single effect: fetch when vehicleFilter changes OR when effectiveMaxDateStr resolves.
  // Waits for effectiveMaxDateStr before the first fetch to avoid a double-load.
  useEffect(() => {
    if (!effectiveMaxDateStr) return

    // Sync dates from the resolved max date
    setCurrentEndDate(effectiveMaxDateStr)
    const maxDate = new Date(effectiveMaxDateStr + 'T12:00:00')
    const prevYear = new Date(maxDate)
    prevYear.setFullYear(prevYear.getFullYear() - 1)
    const prevDateStr = prevYear.toISOString().split('T')[0]
    setPrevEndDate(prevDateStr)

    fetchData({ endDate: effectiveMaxDateStr, prevEndDate: prevDateStr })
  }, [effectiveMaxDateStr, vehicleFilter])

  const fetchData = async (overrides?: { endDate?: string; prevEndDate?: string }) => {
    const resolvedEndDate = overrides?.endDate ?? currentEndDate
    const resolvedPrevEndDate = overrides?.prevEndDate ?? prevEndDate
    try {
      setLoading(true)
      setError(null)

      // Snapshot queried state
      setQueriedCurrentStart(currentStartDate)
      setQueriedCurrentEnd(resolvedEndDate)
      setQueriedPrevStart(prevStartDate)
      setQueriedPrevEnd(resolvedPrevEndDate)
      setQueriedVehicleFilter(vehicleFilter)
      setQueriedPostnummer(minePostnummer)

      const varebilParam = getVarebilParam()
      const params: any = {
        start_date: currentStartDate,
        end_date: resolvedEndDate,
        prev_start_date: prevStartDate,
        prev_end_date: resolvedPrevEndDate,
        limit: 100,
      }

      // Add vehicle filter
      if (varebilParam !== undefined) {
        params.varebil = varebilParam
      }

      // Add postal code filter if enabled
      if (minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      const response = await publicAPI.getTopBrandsComparison(params)

      // Handle different possible response structures
      const data = response.data?.data || response.data || []
      setBrands(Array.isArray(data) ? data : [])
    } catch (err: any) {
      console.error('Brand comparison error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('yoy.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Handle date preset selection - converts Date to string format
  // A preset for the current period also moves the comparison period to the
  // same period last year; editing the comparison afterwards still wins (#861).
  const handleCurrentDatePresetChange = (newStart: Date, newEnd: Date) => {
    const start = newStart.toISOString().split('T')[0]
    const end = newEnd.toISOString().split('T')[0]
    setCurrentStartDate(start)
    setCurrentEndDate(end)
    const prev = samePeriodLastYear(start, end)
    setPrevStartDate(prev.start)
    setPrevEndDate(prev.end)
    setSelectedPrevPreset('')
  }

  const handlePreviousDatePresetChange = (newStart: Date, newEnd: Date) => {
    const prev = samePeriodLastYear(newStart.toISOString().split('T')[0], newEnd.toISOString().split('T')[0])
    setPrevStartDate(prev.start)
    setPrevEndDate(prev.end)
  }

  // Ensure brands is always an array
  const safeBrands = Array.isArray(brands) ? brands : []

  // Sorted brands data - MUST be before early returns
  const sortedBrands = useMemo(() => {
    if (!sortConfig) return safeBrands

    const sorted = [...safeBrands].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (sortConfig.key) {
        case 'rank':
          aVal = a.rank
          bVal = b.rank
          break
        case 'brand':
          aVal = a.brand.toLowerCase()
          bVal = b.brand.toLowerCase()
          break
        case 'previous_sales':
          aVal = toNumber(a.previous_sales)
          bVal = toNumber(b.previous_sales)
          break
        case 'current_sales':
          aVal = toNumber(a.current_sales)
          bVal = toNumber(b.current_sales)
          break
        case 'change':
          aVal = toNumber(a.current_sales) - toNumber(a.previous_sales)
          bVal = toNumber(b.current_sales) - toNumber(b.previous_sales)
          break
        case 'change_percentage':
          aVal = a.change_percentage !== null ? toNumber(a.change_percentage) : -Infinity
          bVal = b.change_percentage !== null ? toNumber(b.change_percentage) : -Infinity
          break
        case 'previous_market_share':
          aVal = toNumber(a.previous_market_share)
          bVal = toNumber(b.previous_market_share)
          break
        case 'current_market_share':
          aVal = toNumber(a.current_market_share)
          bVal = toNumber(b.current_market_share)
          break
        default:
          return 0
      }

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
    })

    return sorted
  }, [safeBrands, sortConfig])

  // Graf tab picks from the brands sold in the current period, largest first.
  const brandOptions = useMemo(
    () => (Array.isArray(brands) ? [...brands] : []).sort((a, b) => a.rank - b.rank).map(b => b.brand),
    [brands]
  )

  const summary = useMemo(() => {
    if (brands.length === 0) return null
    const totalCurrent = brands.reduce((sum, b) => sum + toNumber(b.current_sales), 0)
    const totalPrevious = brands.reduce((sum, b) => sum + toNumber(b.previous_sales), 0)
    const changePct = totalPrevious > 0
      ? ((totalCurrent - totalPrevious) / totalPrevious) * 100
      : null
    return {
      current_sales: totalCurrent,
      previous_sales: totalPrevious,
      change: totalCurrent - totalPrevious,
      change_percentage: changePct,
    }
  }, [brands])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  // Sort handler
  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' })
      } else {
        setSortConfig(null)
      }
    } else {
      setSortConfig({ key, direction: 'asc' })
    }
  }

  // Get sort icon
  const getSortIcon = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' ? 
        <ArrowUp className="w-4 h-4 inline ml-1" /> : 
        <ArrowDown className="w-4 h-4 inline ml-1" />
    }
    return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  }

  // Column definitions for export
  const columns: ColumnDefinition[] = [
    { key: 'rank', label: t('common.rank') },
    { key: 'brand', label: t('common.brand') },
    { 
      key: 'current_sales', 
      label: t('common.currentPeriod'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'previous_sales', 
      label: t('common.previousPeriod'),
      format: (val) => toNumber(val).toLocaleString('nb-NO')
    },
    { 
      key: 'change', 
      label: '+/-',
      format: (val, row?: any) => {
        if (row) {
          const change = row.current_sales - row.previous_sales
          return change.toLocaleString('nb-NO')
        }
        return toNumber(val).toLocaleString('nb-NO')
      }
    },
    { 
      key: 'change_percentage', 
      label: t('common.changePct'),
      format: (val) => val !== null ? `${toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}%` : 'N/A'
    },
    { 
      key: 'current_market_share',
      label: t('yoy.marketShareNowExport'),
      format: (val) => `${toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}%`
    },
    { 
      key: 'previous_market_share',
      label: t('yoy.marketSharePreviousExport'),
      format: (val) => `${toNumber(val).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})}%`
    },
  ]

  // Prepare export data with computed change
  const exportData = sortedBrands.map(b => ({
    ...b,
    change: b.current_sales - b.previous_sales
  }))

  const renderChangeValue = (value: number | null, isPercentage: boolean = false) => {
    if (value === null) {
      return <span className="text-gray-400 dark:text-gray-500">N/A</span>
    }
    
    const color = value > 0 
      ? 'text-green-600 dark:text-green-400' 
      : value < 0 
      ? 'text-red-600 dark:text-red-400' 
      : 'text-gray-600 dark:text-gray-400'
    
    const prefix = value > 0 ? '+' : ''
    const suffix = isPercentage ? '%' : ''
    
    return (
      <span className={`font-semibold ${color}`}>
        {prefix}{value.toLocaleString('nb-NO', {minimumFractionDigits: isPercentage ? 1 : 0, maximumFractionDigits: isPercentage ? 1 : 0})}{suffix}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('yoy.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('yoy.subtitle')}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('common.sourceData')}</p>
      </div>

      {/* Date Range Selector */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('yoy.choosePeriods')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Period */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-700 dark:text-gray-300">
              {t('common.currentPeriod')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('yoy.fromDate')}
                </label>
                <input
                  type="date"
                  value={currentStartDate}
                  onChange={(e) => {
                    const val = e.target.value
                    const maxStr = effectiveMaxDateStr
                    setCurrentStartDate(maxStr && val > maxStr ? maxStr : val)
                  }}
                  max={effectiveMaxDateStr || undefined}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('yoy.toDate')}
                </label>
                <input
                  type="date"
                  value={currentEndDate}
                  max={effectiveMaxDateStr || undefined}
                  onChange={(e) => {
                    const val = e.target.value
                    const maxStr = effectiveMaxDateStr
                    setCurrentEndDate(maxStr && val > maxStr ? maxStr : val)
                  }}
                  className="input-field w-full"
                />
              </div>
            </div>
            <div className="mt-2">
              <DateRangePreset 
                onDateRangeChange={handleCurrentDatePresetChange}
                value={selectedCurrentPreset}
                onValueChange={setSelectedCurrentPreset}
                maxDate={maxDataDate ? new Date(maxDataDate + 'T12:00:00') : undefined}
              />
            </div>
          </div>

          {/* Previous Period */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-700 dark:text-gray-300">
              {t('yoy.comparisonPeriod')}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('yoy.fromDate')}
                </label>
                <input
                  type="date"
                  value={prevStartDate}
                  onChange={(e) => {
                    const val = e.target.value
                    const maxStr = effectiveMaxDateStr
                    setPrevStartDate(maxStr && val > maxStr ? maxStr : val)
                  }}
                  max={effectiveMaxDateStr || undefined}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('yoy.toDate')}
                </label>
                <input
                  type="date"
                  value={prevEndDate}
                  onChange={(e) => {
                    const val = e.target.value
                    const maxStr = effectiveMaxDateStr
                    setPrevEndDate(maxStr && val > maxStr ? maxStr : val)
                  }}
                  max={effectiveMaxDateStr || undefined}
                  className="input-field w-full"
                />
              </div>
            </div>
            <div className="mt-2">
              <DateRangePreset 
                onDateRangeChange={handlePreviousDatePresetChange}
                value={selectedPrevPreset}
                onValueChange={setSelectedPrevPreset}
                maxDate={maxDataDate ? new Date(maxDataDate + 'T12:00:00') : undefined}
              />
            </div>
          </div>
        </div>
        
        {/* Apply Filter Button */}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={() => fetchData()} className="btn-primary">
            {t('common.update')}
          </button>
        </div>

        {/* Postal Code Filter - inside period card */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        </div>
      </div>

      {/* Period Info Banner */}
      {/* <PeriodInfoBanner
        maxDataDate={maxDataDate}
        periodLabel="Sammenligningsperiode"
        startDate={currentStartDate}
        endDate={currentEndDate}
      /> */}

      {/* Query Explanation Box */}
      <QueryExplanationBox
        periods={{
          current: { label: t('common.currentPeriod'), start_date: queriedCurrentStart, end_date: queriedCurrentEnd },
          previous: { label: t('common.previousPeriod'), start_date: queriedPrevStart, end_date: queriedPrevEnd },
        }}
        maxDataDate={maxDataDate}
        outputMode="custom"
        vehicleFilter={queriedVehicleFilter}
        postnummerFilter={queriedPostnummer}
      />

      {/* Tabs (#685) */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border" role="tablist">
        {([
          { key: 'tabell', label: t('periodChart.tabTable'), Icon: Table2 },
          { key: 'graf', label: t('periodChart.tabChart'), Icon: LineChartIcon },
        ] as const).map(({ key, label, Icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === key
                ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <span className="flex items-center gap-2">
              <Icon className="w-4 h-4" />
              {label}
            </span>
          </button>
        ))}
      </div>

      {activeTab === 'graf' && (
        <BrandPeriodChart
          startDate={queriedCurrentStart}
          endDate={queriedCurrentEnd}
          prevStartDate={queriedPrevStart}
          prevEndDate={queriedPrevEnd}
          vehicleFilter={queriedVehicleFilter}
          minePostnummer={queriedPostnummer}
          brandOptions={brandOptions}
        />
      )}

      {/* Full Comparison Table */}
      {activeTab === 'tabell' && (
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            {sortConfig && (
              <button 
                onClick={() => setSortConfig(null)}
                className="btn-secondary text-sm"
              >
                {t('common.resetSort')}
              </button>
            )}
          </div>
          <ExportButtons
            data={exportData}
            columns={columns}
            metadata={{
              title: t('exportTitles.yoy'),
              dateRange: t('yoy.exportDateRange', { prevStart: prevStartDate, prevEnd: prevEndDate, curStart: currentStartDate, curEnd: currentEndDate }),
              filters: [
                vehicleFilter === 'personbil' ? t('common.filterPersonbil') : 
                vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
                minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : ''
              ].filter(f => f),
              explanationText: buildExplanationText({
                periods: {
                  current: { label: t('common.currentPeriod'), start_date: queriedCurrentStart, end_date: queriedCurrentEnd },
                  previous: { label: t('common.previousPeriod'), start_date: queriedPrevStart, end_date: queriedPrevEnd },
                },
                maxDataDate: maxDataDate, outputMode: 'custom', vehicleFilter: queriedVehicleFilter, postnummerFilter: queriedPostnummer,
              }),
            }}
            disabled={loading}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact">
          <thead>
            <tr>
              <th className="text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('rank')} title={t('common.originalRanking')}>
                # {getSortIcon('rank')}
              </th>
              <th className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('brand')}>
                {t('common.brand')} {getSortIcon('brand')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('current_sales')}>
                {t('common.currentPeriod')} {getSortIcon('current_sales')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('previous_sales')}>
                {t('common.previousPeriod')} {getSortIcon('previous_sales')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('change')}>
                +/- {getSortIcon('change')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('change_percentage')}>
                {t('common.changePct')} {getSortIcon('change_percentage')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('current_market_share')}>
                {t('yoy.marketShareNow')} {getSortIcon('current_market_share')}
              </th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('previous_market_share')}>
                {t('yoy.marketSharePrevious')} {getSortIcon('previous_market_share')}
              </th>
            </tr>
          </thead>
          <tbody>
            {summary && (
              <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                <td className="text-center py-2">
                  <span className="text-primary-600 dark:text-primary-400 font-bold">∑</span>
                </td>
                <td className="font-bold text-primary-700 dark:text-primary-300">TOTAL</td>
                <td className="text-right font-bold">{summary.current_sales.toLocaleString('nb-NO')}</td>
                <td className="text-right font-bold">{summary.previous_sales.toLocaleString('nb-NO')}</td>
                <td className="text-right font-bold">
                  {renderChangeValue(summary.change)}
                </td>
                <td className="text-right font-bold">
                  {renderChangeValue(summary.change_percentage, true)}
                </td>
                <td className="text-right font-bold">
                  <span className="text-primary-600 dark:text-primary-400">100,0 %</span>
                </td>
                <td className="text-right font-bold">
                  <span className="text-primary-600 dark:text-primary-400">100,0 %</span>
                </td>
              </tr>
            )}
            {sortedBrands.map((brand) => (
              <tr key={brand.brand}>
                <td className="text-center text-gray-700 dark:text-gray-300">
                  {brand.rank}
                </td>
                <td className="font-semibold">{brand.brand}</td>
                <td className="text-right font-medium">{toNumber(brand.current_sales).toLocaleString('nb-NO')}</td>
                <td className="text-right">{toNumber(brand.previous_sales).toLocaleString('nb-NO')}</td>
                <td className="text-right">
                  {renderChangeValue(toNumber(brand.current_sales) - toNumber(brand.previous_sales))}
                </td>
                <td className="text-right">
                  {renderChangeValue(brand.change_percentage, true)}
                </td>
                <td className="text-right text-sm font-medium">
                  {toNumber(brand.current_market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %
                </td>
                <td className="text-right text-sm">{toNumber(brand.previous_market_share).toLocaleString('nb-NO', {minimumFractionDigits:1, maximumFractionDigits:1})} %</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      )}
    </div>
  )
}
